import { cn } from '@/lib/utils';
import { Brain, ArrowRight } from 'lucide-react';
import type { ExtractedEntity, ExtractedRelationship } from './useInterviewStore';

/** All built-in types from the Core Ontology domain (v2) */
const CORE_META_TYPES = new Set([
  'Entity', 'Process', 'Source', 'Rule', 'MasterData',
  'Actor', 'Spatial', 'Asset', 'Information', 'Temporal',
  'DataPoint', 'Quality', 'Situation',
  'Observation', 'ObservableProperty', 'Capability',
  'PhysicalObject', 'Plan', 'Agent', 'Role',
]);

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  // Legacy base
  Entity:             { bg: 'bg-blue-500/10',    text: 'text-blue-600',    border: 'border-blue-500/20' },
  Process:            { bg: 'bg-green-500/10',   text: 'text-green-600',   border: 'border-green-500/20' },
  Rule:               { bg: 'bg-amber-500/10',   text: 'text-amber-600',   border: 'border-amber-500/20' },
  Source:             { bg: 'bg-purple-500/10',  text: 'text-purple-600',  border: 'border-purple-500/20' },
  // Core v2
  Actor:              { bg: 'bg-blue-400/10',    text: 'text-blue-500',    border: 'border-blue-400/20' },
  Agent:              { bg: 'bg-blue-600/10',    text: 'text-blue-600',    border: 'border-blue-600/20' },
  PhysicalObject:     { bg: 'bg-sky-500/10',     text: 'text-sky-600',     border: 'border-sky-500/20' },
  Role:               { bg: 'bg-blue-300/10',    text: 'text-blue-400',    border: 'border-blue-300/20' },
  Spatial:            { bg: 'bg-violet-500/10',  text: 'text-violet-600',  border: 'border-violet-500/20' },
  Asset:              { bg: 'bg-indigo-500/10',  text: 'text-indigo-600',  border: 'border-indigo-500/20' },
  Plan:               { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20' },
  Information:        { bg: 'bg-purple-400/10',  text: 'text-purple-500',  border: 'border-purple-400/20' },
  DataPoint:          { bg: 'bg-purple-300/10',  text: 'text-purple-400',  border: 'border-purple-300/20' },
  Quality:            { bg: 'bg-pink-500/10',    text: 'text-pink-600',    border: 'border-pink-500/20' },
  Situation:          { bg: 'bg-orange-500/10',  text: 'text-orange-600',  border: 'border-orange-500/20' },
  MasterData:         { bg: 'bg-yellow-500/10',  text: 'text-yellow-600',  border: 'border-yellow-500/20' },
  Temporal:           { bg: 'bg-rose-400/10',    text: 'text-rose-500',    border: 'border-rose-400/20' },
  // SOSA
  Observation:        { bg: 'bg-teal-500/10',    text: 'text-teal-600',    border: 'border-teal-500/20' },
  ObservableProperty: { bg: 'bg-teal-400/10',    text: 'text-teal-500',    border: 'border-teal-400/20' },
  Capability:         { bg: 'bg-cyan-500/10',    text: 'text-cyan-600',    border: 'border-cyan-500/20' },
};

const DOMAIN_FALLBACK = { bg: 'bg-orange-500/10', text: 'text-orange-500', border: 'border-orange-500/20 border-dashed' };

function getColors(type: string) {
  return TYPE_COLORS[type] ?? DOMAIN_FALLBACK;
}

const TYPE_LABELS: Record<string, string> = {
  Entity: 'Entität', Process: 'Prozess', Rule: 'Regel', Source: 'Quelle',
  Actor: 'Akteur', Agent: 'Agent', PhysicalObject: 'Objekt', Role: 'Rolle',
  Spatial: 'Ort', Asset: 'Asset', Plan: 'Plan', Information: 'Information',
  DataPoint: 'Datenpunkt', Quality: 'Qualität', Situation: 'Situation',
  MasterData: 'Stammdaten', Temporal: 'Zeit', Observation: 'Messung',
  ObservableProperty: 'Merkmal', Capability: 'Fähigkeit',
};

interface ExtractedEntitiesPreviewProps {
  entities: ExtractedEntity[];
  relationships?: ExtractedRelationship[];
  className?: string;
}

export function ExtractedEntitiesPreview({ entities, relationships = [], className }: ExtractedEntitiesPreviewProps) {
  const grouped = entities.reduce<Record<string, ExtractedEntity[]>>((acc, e) => {
    const type = e.type || 'Entity';
    if (!acc[type]) acc[type] = [];
    acc[type].push(e);
    return acc;
  }, {});

  // Build a lookup: source entity name → its relationships
  const relsBySource = relationships.reduce<Record<string, ExtractedRelationship[]>>((acc, r) => {
    if (!acc[r.source]) acc[r.source] = [];
    acc[r.source].push(r);
    return acc;
  }, {});

  const totalCount = entities.length;

  return (
    <div className={cn('flex h-full w-64 flex-col border-l border-border bg-muted/30', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Brain className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">
          Extrahiert{' '}
          <span className="text-muted-foreground font-normal">({totalCount})</span>
        </h3>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {totalCount === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8">
            Während des Interviews werden hier automatisch erkannte Entitäten angezeigt.
          </p>
        )}

        {Object.entries(grouped).map(([type, items]) => {
          const colors = getColors(type);
          const label = TYPE_LABELS[type] ?? type;
          const isCore = CORE_META_TYPES.has(type);

          return (
            <div key={type}>
              <p className={cn('mb-1.5 text-[11px] font-semibold uppercase tracking-wider', colors.text)}>
                {label} ({items.length})
                {!isCore && <span className="ml-1 font-normal opacity-60 normal-case">domain</span>}
              </p>
              <ul className="space-y-1">
                {items.map((entity, idx) => (
                  <li
                    key={`${entity.name}-${idx}`}
                    className={cn(
                      'rounded-md border px-2.5 py-1.5 text-xs',
                      colors.bg,
                      colors.border,
                    )}
                  >
                    <span className="font-medium text-foreground">{entity.name}</span>
                    {entity.description && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground line-clamp-2">
                        {entity.description}
                      </p>
                    )}
                    {relsBySource[entity.name] && relsBySource[entity.name].length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {relsBySource[entity.name].map((rel, rIdx) => (
                          <div
                            key={rIdx}
                            className="flex items-center gap-1 text-[10px] text-muted-foreground"
                          >
                            <ArrowRight className="h-2.5 w-2.5" />
                            <span className="font-medium">{rel.type}</span>
                            <span>→ {rel.target}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
